import React, { useLayoutEffect, useState } from "react";
import HTMLReactParser from "html-react-parser";
import { decodeHTML } from "entities";

const isBetween = (value: number, floor: number, ceil: number) =>
  value >= floor && value <= ceil;

const clamp = (value: number) => Math.max(0, value);

export const useScrollspy = (
  menus: any,
  offset: any,
  defaultValue: any,
  elementKey: string
) => {
  const [activeId, setActiveId] = useState(defaultValue);

  useLayoutEffect(() => {
    const listener = () => {
      const scroll = window.scrollY;

      const position = menus
        ?.map((menu: { [x: string]: any }) => {
          const element = document.getElementById(menu[elementKey]);

          if (!element)
            return { [elementKey]: menu[elementKey], top: -1, bottom: -1 };

          const rect = element.getBoundingClientRect();
          const top = clamp(rect.top + scroll - offset);
          const bottom = clamp(rect.bottom + scroll - offset);

          return { [elementKey]: menu[elementKey], top, bottom };
        })
        .find(({ top, bottom }: { top: number; bottom: number }) =>
          isBetween(scroll, top, bottom)
        );

      setActiveId(position?.[elementKey] || defaultValue);
    };

    listener();

    window.addEventListener("resize", listener);
    window.addEventListener("scroll", listener);

    return () => {
      window.removeEventListener("resize", listener);
      window.removeEventListener("scroll", listener);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menus, offset]);

  return activeId;
};

export const getFormattedIndex = (num: { toString: () => string }) => {
  return num.toString().padStart(2, "0");
};

export const linkActions = {
  REDIRECT: "Redirect",
  CLICK: "Popup",
};

export const parseHtml = (htmlString = "", checkP = false) => {
  if (checkP && !htmlString.startsWith("<p>") && !htmlString.endsWith("</p>")) {
    return <p className="text-light-black">{HTMLReactParser(htmlString)}</p>;
  }
  return htmlString ? HTMLReactParser(htmlString) : "";
};

// Wraps bold (<strong>/<b>) and "quoted" text in <span> tags so the
// existing `h1 span` gradient-color rule (see globals.css) picks it up.
export const highlightBoldOrQuoted = (htmlString = "") => {
  if (!htmlString) return "";
  return htmlString
    .replace(/<(strong|b)>(.*?)<\/\1>/gi, "<span>$2</span>")
    .replace(/["“]([^"”]+)["”]/g, "<span>$1</span>");
};

export const parseHtmlWithHighlight = (htmlString = "", checkP = false) =>
  parseHtml(highlightBoldOrQuoted(htmlString), checkP);

export const acceptedFileTypes = [
  "image/jpeg",
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

export const decodeHTMLEntities = (str: string) => {
  const element = decodeHTML(str);
  return element;
};

export const useCheckYOffset = (
  pageOffSet = 0,
  eleClass = "",
  operator = ">",
) => {
  const [elementClass, setElementClass] = useState("");

  useLayoutEffect(() => {
    const listener = () => {
      const scroll = window.pageYOffset;

      if (eval(`${scroll} ${operator} ${pageOffSet}`)) {
        setElementClass(eleClass);
      } else {
        setElementClass("");
      }
    };
    listener();

    window.addEventListener("scroll", listener);
    return () => {
      window.removeEventListener("scroll", listener);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return elementClass;
};

export const PHONE_REGEX = /^[0-9+() ]{0,14}$/;

export const SLIDER_STEPS = 500;

export const scrollElementIntoView = (id: string) => {
  document.getElementById(id)?.scrollIntoView();
};

// export const toWebp = (imgURL,quality=90) => {
// 	if (typeof imgURL === "string") {
// 		const isSvg = imgURL?.toLowerCase().endsWith(".svg");
// 		if (!isSvg) return imgURL?.replace("uploads", "uploads/f_webp");
// 		return imgURL;
// 	}
// 	return imgURL;
// };
export const toWebp = (imgURL: string, quality = 90) => {
  if (!imgURL || typeof imgURL !== "string") {
    // Return the input if it's not a valid string
    return imgURL;
  }

  // Guard against callers building the URL via string interpolation
  // (e.g. `${baseUrl}${data?.image?.url}`), which turns a missing url
  // into the literal substring "undefined"/"null" instead of falsy value
  if (/\b(undefined|null)\b/.test(imgURL)) {
    return "/images/placeholder.png";
  }

  // Check if the image is an SVG
  const isSvg =
    imgURL.toLowerCase().includes(".svg") ||
    imgURL.toLowerCase().includes(".webp");

  // Skip conversion for SVG files
  if (isSvg) {
    return imgURL;
  }

  // Replace "uploads" with "uploads/f_webp" in the URL
  const updatedPath = imgURL.replace("uploads", "uploads/f_webp");

  // Check if the URL already has query parameters
  const hasQuery = imgURL.includes("?");

  // Add quality as a query parameter if needed
  const webpPath = `${updatedPath}${hasQuery ? "&" : "?"}q=${quality}`;

  return webpPath;
};

export const getCookie = (cname: string) => {
  if (cname) {
    const name = cname + "=";
    const decodedCookie = decodeURIComponent(document.cookie);
    const ca = decodedCookie.split(";");
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) == " ") {
        c = c.substring(1);
      }
      if (c.indexOf(name) == 0) {
        return c.substring(name.length, c.length);
      }
    }
  }
  return "";
};

export const setCookie = (cname: string, cvalue: string, exdays = 365) => {
  const d = new Date();
  d.setTime(d.getTime() + exdays * 24 * 60 * 60 * 1000);
  document.cookie = `${cname}=${cvalue};expires=${d.toUTCString()};path=/`;
};

// Resolves an article to its real route prefix ("blog" | "whitepaper" | "newsroom"),
// mirroring the detection logic used by the post sitemap.
export const getArticleRoutePrefix = (article: any): string => {
  const blogTypeSlugs: string[] =
    article?.blog_types?.data?.map((bt: any) => bt?.attributes?.slug) || [];

  if (blogTypeSlugs.includes("whitepaper")) return "whitepaper";
  if (blogTypeSlugs.includes("newsroom")) return "newsroom";

  const articleType: string = (article?.article_type || "").toLowerCase();
  if (articleType.includes("whitepaper")) return "whitepaper";
  if (articleType.includes("newsroom")) return "newsroom";

  return "blog";
};

export const getArticleUrl = (item: any) =>
  `/${getArticleRoutePrefix(item?.attributes)}/${item?.attributes?.slug ?? ""}`;

export const getHref = (eleLink = "/") => {
  if (!!eleLink) {
    if (eleLink?.charAt(0) === "/") {
      return eleLink;
    }
    return "/" + eleLink;
  }
  return "/#";
};

export const parseHtml1 = (htmlString = "", checkP = false) => {
  if (checkP && !htmlString.startsWith("<p>") && !htmlString.endsWith("</p>")) {
    return <p>{HTMLReactParser(htmlString)}</p>;
  }
  return htmlString ? HTMLReactParser(htmlString) : "";
};

export const downloadFile = async (url = "", name: string) => {
  try {
    const baseUrl = process.env.NEXT_PUBLIC_REACT_APP_RESOURCE_BASE_URL;
    const response = await fetch(baseUrl + url, {
      method: "GET",
      headers: { "Content-Type": "application/octet-stream" },
    });

    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = name ?? "downloaded-file";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    window.URL.revokeObjectURL(blobUrl);
  } catch (error) {
    console.error("Download failed:", error);
  }
};

export const removeSlash = (link = "") =>
  link.charAt(0) === "/" ? link.slice(1) : link;
